import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import type { PrjSchedule } from 'models/setup/PrjSchedule';
import { getScheduleStatus, getScheduleStyle } from 'utils/CalendarUtils';
import {
  CalBoard,
  CalGridHeader,
  CalHeaderCell,
  CalGridBody,
  CalGridRow,
  CalDayCell,
  CalCellHeader,
  CalDateNum,
  CalEventsStack,
  CalEventChip,
  CalMoreBtn,
  IncompleteAlertBadge,
  DelayedTagBadge,
  CalEventTitleText,
} from 'components/calendar/Calendar.styles';
import { EventMoreLayer } from 'components/calendar/EventMoreLayer';
import { useTranslation } from 'react-i18next';

dayjs.extend(isBetween);

interface MonthViewProps {
  currentDate: Dayjs;
  schedules: PrjSchedule[];
  selectedCategories: Set<string>;
  isExpanded?: boolean;
  onSelectSchedule: (schedule: PrjSchedule) => void;
  onAddScheduleOnDate: (dateStr: string) => void;
  onShowMoreSchedules?: (dateStr: string, daySchedules: PrjSchedule[]) => void;
  onMoveScheduleDate: (schdId: string, targetDateStr: string) => void;
}

const MAX_VISIBLE_EVENTS = 3;

export const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  schedules,
  selectedCategories,
  isExpanded = false,
  onSelectSchedule,
  onAddScheduleOnDate,
  onShowMoreSchedules,
  onMoveScheduleDate,
}) => {
  const today = dayjs();
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);
  const { t } = useTranslation();

  const WEEK_DAYS = [
    String(t('day.label.일', '일')),
    String(t('day.label.월', '월')),
    String(t('day.label.화', '화')),
    String(t('day.label.수', '수')),
    String(t('day.label.목', '목')),
    String(t('day.label.금', '금')),
    String(t('day.label.토', '토')),
  ];
  // 더보기 팝오버 레이어 상태
  const [moreLayerState, setMoreLayerState] = useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
    dateStr: string;
    schedules: PrjSchedule[];
  }>({
    open: false,
    anchorEl: null,
    dateStr: '',
    schedules: [],
  });

  // Calculate calendar grid (starts on Sunday of first week, ends on Saturday of last week)
  const weeks = useMemo(() => {
    const startOfMonth = currentDate.startOf('month');
    const endOfMonth = currentDate.endOf('month');
    const startDay = startOfMonth.day(); // 0 = Sun
    const totalDaysInMonth = endOfMonth.date();

    // Prev month filler days
    const prevDays: Dayjs[] = [];
    for (let i = startDay - 1; i >= 0; i--) {
      prevDays.push(startOfMonth.subtract(i + 1, 'day'));
    }

    // Current month days
    const currentMonthDays: Dayjs[] = [];
    for (let d = 1; d <= totalDaysInMonth; d++) {
      currentMonthDays.push(currentDate.date(d));
    }

    // Next month filler days
    const allDaysCount = prevDays.length + currentMonthDays.length;
    const targetCells = allDaysCount > 35 ? 42 : 35;
    const remainingDays = targetCells - allDaysCount;
    const nextDays: Dayjs[] = [];
    for (let i = 1; i <= remainingDays; i++) {
      nextDays.push(endOfMonth.add(i, 'day'));
    }

    const allCalendarDays = [...prevDays, ...currentMonthDays, ...nextDays];

    // Group into weeks (rows of 7 days)
    const weekRows: Dayjs[][] = [];
    for (let i = 0; i < allCalendarDays.length; i += 7) {
      weekRows.push(allCalendarDays.slice(i, i + 7));
    }
    return weekRows;
  }, [currentDate]);

  // Precompute schedules per day using Map for O(1) lookup
  const daySchedulesMap = useMemo(() => {
    const map = new Map<string, PrjSchedule[]>();
    for (const week of weeks) {
      for (const day of week) {
        map.set(day.format('YYYY-MM-DD'), []);
      }
    }

    for (const sch of schedules) {
      if (!sch.planStDtm || !sch.planEndDtm) continue;
      const start = dayjs(sch.planStDtm).format('YYYY-MM-DD');
      const end = dayjs(sch.planEndDtm).format('YYYY-MM-DD');

      map.forEach((list, dayStr) => {
        if (dayStr >= start && dayStr <= end) {
          list.push(sch);
        }
      });
    }
    return map;
  }, [schedules, weeks]);

  // Filter schedules for a specific day
  const getSchedulesForDay = (day: Dayjs): PrjSchedule[] => {
    return daySchedulesMap.get(day.format('YYYY-MM-DD')) || [];
  };

  // Drag & Drop handlers
  const handleDragStart = (e: React.DragEvent, schedule: PrjSchedule, sourceDate?: string) => {
    if (!schedule.schdId || schedule.schdStatCd === 'CPLT') {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('text/plain', schedule.schdId);
    if (sourceDate) {
      e.dataTransfer.setData('application/x-source-date', sourceDate);
    }
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, dayStr: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverDate !== dayStr) {
      setDragOverDate(dayStr);
    }
  };

  const handleDragLeave = (_e: React.DragEvent, dayStr: string) => {
    if (dragOverDate === dayStr) {
      setDragOverDate(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetDateStr: string) => {
    e.preventDefault();
    setDragOverDate(null);
    const schdId = e.dataTransfer.getData('text/plain');
    const sourceDate = e.dataTransfer.getData('application/x-source-date');

    if (!schdId) return;

    const targetSchedule = schedules.find((s) => s.schdId === schdId);
    if (!targetSchedule) return;

    const origStartDate = targetSchedule.planStDtm
      ? dayjs(targetSchedule.planStDtm).format('YYYY-MM-DD')
      : '';

    // 원래 날짜 위치로 놓는 경우(원래 시작 날짜이거나 드래그를 시작한 날짜 셀과 동일한 경우) 아무런 동작을 하지 않음
    if (targetDateStr === origStartDate || (sourceDate && targetDateStr === sourceDate)) {
      return;
    }

    onMoveScheduleDate(schdId, targetDateStr);
  };

  return (
    <CalBoard isExpanded={isExpanded}>
      {/* Header (일, 월, 화, 수, 목, 금, 토) */}
      <CalGridHeader>
        {WEEK_DAYS.map((dayName, idx) => (
          <CalHeaderCell key={dayName} isSunday={idx === 0} isSaturday={idx === 6}>
            {dayName}
          </CalHeaderCell>
        ))}
      </CalGridHeader>

      {/* Grid rows */}
      <CalGridBody weekCount={weeks.length} isExpanded={isExpanded}>
        {weeks.map((week, wIdx) => (
          <CalGridRow key={`week-${wIdx}`} isExpanded={isExpanded}>
            {week.map((day, dIdx) => {
              const dayStr = day.format('YYYY-MM-DD');
              const isCurrentMonth = day.month() === currentDate.month();
              const isToday = day.isSame(today, 'day');
              const isSunday = dIdx === 0;
              const isSaturday = dIdx === 6;
              const daySchedules = getSchedulesForDay(day);
              const visibleSchedules = isExpanded
                ? daySchedules
                : daySchedules.slice(0, MAX_VISIBLE_EVENTS);
              const moreCount = isExpanded ? 0 : daySchedules.length - MAX_VISIBLE_EVENTS;
              const isDraggingOver = dragOverDate === dayStr;

              return (
                <CalDayCell
                  key={dayStr}
                  isOtherMonth={!isCurrentMonth}
                  isToday={isToday}
                  isSunday={isSunday}
                  isSaturday={isSaturday}
                  isDragOver={isDraggingOver}
                  isExpanded={isExpanded}
                  onDragOver={(e) => handleDragOver(e, dayStr)}
                  onDragLeave={(e) => handleDragLeave(e, dayStr)}
                  onDrop={(e) => handleDrop(e, dayStr)}
                  onClick={(e) => {
                    if (
                      e.target === e.currentTarget ||
                      (e.target as HTMLElement).tagName === 'DIV' ||
                      (e.target as HTMLElement).tagName === 'SPAN'
                    ) {
                      onAddScheduleOnDate(dayStr);
                    }
                  }}
                >
                  <CalCellHeader>
                    <CalDateNum
                      isToday={isToday}
                      isSunday={isSunday}
                      isOtherMonth={!isCurrentMonth}
                    >
                      {day.date()}
                    </CalDateNum>
                  </CalCellHeader>

                  <CalEventsStack isExpanded={isExpanded}>
                    {visibleSchedules.map((sch) => {
                      const category = sch.wktTpCd || 'ETC';
                      const isDimmed = !selectedCategories.has(category);
                      const status = getScheduleStatus(sch, dayStr);
                      const style = getScheduleStyle(status, sch.wktTpCd, isDimmed);
                      const displayTitle = sch.schdDisplayTitle || sch.schdNm || '일정';
                      const showProcAlert = sch.schdProcYn === 'Y';
                      const tooltipContent = `${displayTitle} (${dayjs(sch.planStDtm).format(
                        'YYYY-MM-DD'
                      )} ~ ${dayjs(sch.planEndDtm).format('YYYY-MM-DD')})`;

                      return (
                        <CalEventChip
                          key={`${dayStr}-${sch.schdId}`}
                          title={tooltipContent}
                          draggable={sch.schdStatCd !== 'CPLT'}
                          statusType={status}
                          wktTpCd={sch.wktTpCd}
                          isDimmed={isDimmed}
                          onDragStart={(e) => handleDragStart(e, sch, dayStr)}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectSchedule(sch);
                          }}
                        >
                          {showProcAlert && (
                            <IncompleteAlertBadge title="진행 중">!</IncompleteAlertBadge>
                          )}

                          <CalEventTitleText>{displayTitle}</CalEventTitleText>

                          {/* 1번 대안 확정: 지연 일정 미니 뱃지 */}
                          {style.showDelayedBadge && (
                            <DelayedTagBadge>{t('com.label.지연', '지연')}</DelayedTagBadge>
                          )}
                        </CalEventChip>
                      );
                    })}

                    {/* 4번 요구사항: +(더보기) 버튼 기능 */}
                    {moreCount > 0 && (
                      <CalMoreBtn
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMoreLayerState({
                            open: true,
                            anchorEl: e.currentTarget,
                            dateStr: dayStr,
                            schedules: daySchedules,
                          });
                          if (onShowMoreSchedules) {
                            onShowMoreSchedules(dayStr, daySchedules);
                          }
                        }}
                      >
                        +{moreCount}
                      </CalMoreBtn>
                    )}
                  </CalEventsStack>
                </CalDayCell>
              );
            })}
          </CalGridRow>
        ))}
      </CalGridBody>

      {/* 4번 요구사항: 화면 밖으로 잘리지 않는 더보기 레이어 */}
      <EventMoreLayer
        open={moreLayerState.open}
        anchorEl={moreLayerState.anchorEl}
        dateStr={moreLayerState.dateStr}
        schedules={moreLayerState.schedules}
        selectedCategories={selectedCategories}
        onClose={() => setMoreLayerState((prev) => ({ ...prev, open: false, anchorEl: null }))}
        onSelectSchedule={onSelectSchedule}
      />
    </CalBoard>
  );
};
